<template>
  <div class="cost-report-wrapper">
    <!-- Header -->
    <div class="report-header">
      <div class="report-header-left">
        <v-btn icon @click="$router.back()" class="back-btn">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="report-title">AI Cost Report</div>
          <div class="report-subtitle" v-if="lastUpdated">
            Last updated: {{ lastUpdated }}
          </div>
        </div>
      </div>
      <v-btn icon :loading="loading" @click="loadData" class="refresh-btn">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <div class="filters-row">
        <!-- Date Range -->
        <v-menu
          v-model="fromMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          class="date-picker-menu"
          max-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="filters.fromDate"
              label="From Date"
              prepend-inner-icon="mdi-calendar"
              readonly
              outline
              dense
              hide-details
              class="filter-field"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            class="date-picker-menu"
            v-model="filters.fromDate"
            no-title
            @input="fromMenu = false"
          ></v-date-picker>
        </v-menu>

        <v-menu
          v-model="toMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          class="date-picker-menu"
          offset-y
          max-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="filters.toDate"
              label="To Date"
              prepend-inner-icon="mdi-calendar"
              readonly
              outline
              dense
              hide-details
              class="filter-field"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="filters.toDate"
            no-title
            @input="toMenu = false"
          ></v-date-picker>
        </v-menu>

        <!-- Caller Filter -->
        <v-autocomplete
          v-model="filters.caller"
          :items="callerOptions"
          item-text="text"
          item-value="value"
          label="Caller / User"
          prepend-inner-icon="mdi-account-search"
          outline
          dense
          hide-details
          clearable
          class="filter-field"
        ></v-autocomplete>

        <!-- Model Filter -->
        <v-select
          v-model="filters.model"
          :items="modelOptions"
          label="Model"
          prepend-inner-icon="mdi-robot"
          outline
          dense
          hide-details
          clearable
          class="filter-field filter-field-sm"
        ></v-select>

        <!-- Group By -->
        <v-select
          v-model="groupBy"
          :items="groupByOptions"
          label="Group By"
          prepend-inner-icon="mdi-group"
          outline
          dense
          hide-details
          style="max-width: 320px; min-width: 200px;"
          class="filter-field filter-field-sm"
        ></v-select>

        <!-- Apply -->
        <v-btn
          color="green"
          dark
          @click="loadData"
          :loading="loading"
          class="apply-btn"
        >
          <v-icon left>mdi-magnify</v-icon>
          Apply
        </v-btn>
      </div>

      <!-- Quick Date Ranges -->
      <div class="quick-ranges">
        <span class="quick-label">Quick:</span>
        <v-btn x-small text color="green" @click="setRange('today')"
          >Today</v-btn
        >
        <v-btn x-small text color="green" @click="setRange('week')"
          >This Week</v-btn
        >
        <v-btn x-small text color="green" @click="setRange('month')"
          >This Month</v-btn
        >
        <v-btn x-small text color="green" @click="setRange('30days')"
          >Last 30 Days</v-btn
        >
        <v-btn x-small text color="green" @click="setRange('90days')"
          >Last 90 Days</v-btn
        >
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular
        indeterminate
        color="green"
        size="50"
      ></v-progress-circular>
      <div class="mt-3">Loading report...</div>
    </div>

    <template v-else>
      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="summary-card">
          <div class="summary-icon green">
            <v-icon color="white">mdi-currency-usd</v-icon>
          </div>
          <div class="summary-content">
            <div class="summary-value">${{ totalCost.toFixed(4) }}</div>
            <div class="summary-label">Total Cost</div>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-icon blue">
            <v-icon color="white">mdi-comment-multiple</v-icon>
          </div>
          <div class="summary-content">
            <div class="summary-value">
              {{ totalQuestions.toLocaleString() }}
            </div>
            <div class="summary-label">Total Questions</div>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-icon orange">
            <v-icon color="white">mdi-calculator</v-icon>
          </div>
          <div class="summary-content">
            <div class="summary-value">${{ avgCost.toFixed(4) }}</div>
            <div class="summary-label">Avg Cost / Question</div>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-icon purple">
            <v-icon color="white">mdi-account-multiple</v-icon>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ uniqueCallers }}</div>
            <div class="summary-label">Unique Users</div>
          </div>
        </div>
      </div>

      <!-- Data Table + Chart -->
      <div class="table-section">
        <div class="table-header-row">
          <div class="table-title">
            <v-icon small class="mr-2" color="green">mdi-table</v-icon>
            Cost Breakdown — Grouped by {{ groupByLabel }}
          </div>
          <v-text-field
            v-model="tableSearch"
            placeholder="Search results..."
            prepend-inner-icon="mdi-magnify"
            hide-details
            clearable
            solo
            flat
            class="table-search"
          ></v-text-field>
        </div>

        <div class="table-chart-row">
          <v-data-table
            :headers="tableHeaders"
            :items="filteredTableData"
            :pagination.sync="tablePagination"
            class="cost-table cost-table-padded breakdown-table"
          >
            <template slot="items" slot-scope="{ item }">
              <tr>
                <td class="td-padded">{{ item.label }}</td>
                <td class="td-padded">{{ item.count }}</td>
                <td class="td-padded">
                  <span class="cost-value">${{ item.totalCost.toFixed(4) }}</span>
                </td>
                <td class="td-padded">
                  <span class="cost-value-light"
                    >${{ item.avgCost.toFixed(4) }}</span
                  >
                </td>
                <td class="td-padded">
                  <div class="percentage-cell">
                    <div class="percentage-bar-bg">
                      <div
                        class="percentage-bar"
                        :style="{ width: item.percentage + '%' }"
                      ></div>
                    </div>
                    <span class="percentage-text"
                      >{{ item.percentage.toFixed(1) }}%</span
                    >
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>

          <!-- Donut Chart -->
          <div class="chart-panel" v-if="chartSlices.length">
            <div class="chart-title">Cost Distribution</div>
            <div class="donut-wrapper">
              <svg viewBox="0 0 200 200" width="220" height="220">
                <!-- Background ring -->
                <circle cx="100" cy="100" r="70" fill="none" stroke="#f0f0f0" stroke-width="36" />
                <!-- Slices: start from top (rotate -90 on the group) -->
                <g transform="rotate(-90 100 100)">
                  <circle
                    v-for="(slice, i) in chartSlices"
                    :key="'slice-' + i"
                    cx="100" cy="100" r="70"
                    fill="none"
                    :stroke="slice.color"
                    stroke-width="36"
                    :stroke-dasharray="slice.dash + ' ' + slice.gap"
                    :stroke-dashoffset="slice.offset"
                  />
                </g>
                <!-- White center hole -->
                <circle cx="100" cy="100" r="52" fill="white" />
                <!-- Center text (not rotated) -->
                <text x="100" y="95" text-anchor="middle" font-size="16" font-weight="700" fill="#333">${{ totalCost.toFixed(2) }}</text>
                <text x="100" y="113" text-anchor="middle" font-size="10" fill="#999">Total Cost</text>
              </svg>
            </div>
            <div class="chart-legend">
              <div
                v-for="(slice, i) in chartSlices"
                :key="'legend-' + i"
                class="legend-item"
              >
                <span class="legend-dot" :style="{ background: slice.color }"></span>
                <span class="legend-label">{{ slice.label }}</span>
                <span class="legend-pct">{{ slice.pct.toFixed(1) }}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- All Records -->
      <!-- Usage Trend Chart -->
      <div class="trend-section">
        <div class="trend-header">
          <div class="trend-header-left">
            <div class="table-title">
              <v-icon small class="mr-2" color="#2196f3">mdi-chart-line</v-icon>
              Usage Over Time
            </div>
            <div class="trend-quick-ranges">
              <span :class="['trend-range-btn', { active: trendActiveRange === 'today' }]" @click="setTrendRange('today')">Today</span>
              <span :class="['trend-range-btn', { active: trendActiveRange === '7days' }]" @click="setTrendRange('7days')">Last 7d</span>
              <span :class="['trend-range-btn', { active: trendActiveRange === '30days' }]" @click="setTrendRange('30days')">Last 30d</span>
              <span :class="['trend-range-btn', { active: trendActiveRange === '90days' }]" @click="setTrendRange('90days')">Last 90d</span>
              <span :class="['trend-range-btn', { active: trendActiveRange === 'month' }]" @click="setTrendRange('month')">This Month</span>
            </div>
          </div>
          <v-autocomplete
            v-model="trendUserFilter"
            :items="callerOptions"
            item-text="text"
            item-value="value"
            placeholder="All Users"
            solo
            flat
            dense
            hide-details
            clearable
            class="table-search trend-user-field"
          ></v-autocomplete>
        </div>

        <div v-if="trendSvgData" class="trend-chart-wrapper">
          <svg
            :viewBox="`0 0 ${trendSvgData.W} ${trendSvgData.H}`"
            class="trend-svg"
            preserveAspectRatio="xMidYMid meet"
          >
            <!-- Horizontal grid lines -->
            <line
              v-for="(yl, i) in trendSvgData.yLabels"
              :key="'grid-' + i"
              :x1="trendSvgData.padL"
              :y1="yl.y"
              :x2="trendSvgData.W - trendSvgData.padR"
              :y2="yl.y"
              stroke="#f0f0f0"
              stroke-width="1"
            />
            <!-- Y axis labels -->
            <text
              v-for="(yl, i) in trendSvgData.yLabels"
              :key="'ylabel-' + i"
              :x="trendSvgData.padL - 8"
              :y="yl.y + 4"
              text-anchor="end"
              font-size="11"
              fill="#bbb"
            >{{ yl.val }}</text>
            <!-- Area fill -->
            <path
              :d="trendSvgData.areaPath"
              fill="#2196f3"
              opacity="0.12"
            />
            <!-- Line -->
            <polyline
              :points="trendSvgData.linePoints"
              fill="none"
              stroke="#2196f3"
              stroke-width="2.5"
              stroke-linejoin="round"
              stroke-linecap="round"
            />
            <!-- Data point circles -->
            <circle
              v-for="(pt, i) in trendSvgData.pts"
              :key="'pt-' + i"
              :cx="pt.x"
              :cy="pt.y"
              r="3.5"
              fill="#2196f3"
              stroke="white"
              stroke-width="1.5"
            />
            <!-- X axis labels -->
            <text
              v-for="(xl, i) in trendSvgData.xLabels"
              :key="'xlabel-' + i"
              :x="xl.x"
              :y="trendSvgData.H - trendSvgData.padB + 18"
              text-anchor="middle"
              font-size="11"
              fill="#bbb"
            >{{ xl.label }}</text>
            <!-- Baseline -->
            <line
              :x1="trendSvgData.padL"
              :y1="trendSvgData.padT + trendSvgData.chartH"
              :x2="trendSvgData.W - trendSvgData.padR"
              :y2="trendSvgData.padT + trendSvgData.chartH"
              stroke="#e0e0e0"
              stroke-width="1.5"
            />
          </svg>
        </div>
        <div v-else class="trend-empty">Not enough data to display trend</div>
      </div>

      <div class="raw-section">
        <div class="raw-section-header">
          <div class="table-title">
            <v-icon small class="mr-2" color="green"
              >mdi-format-list-bulleted</v-icon
            >
            All Records
          </div>
          <div class="raw-filters-row">
            <v-text-field
              v-model="rawSearch"
              placeholder="Search question..."
              prepend-inner-icon="mdi-magnify"
              solo
              flat
              dense
              hide-details
              clearable
              class="table-search"
            ></v-text-field>
            <v-autocomplete
              v-model="rawFilterCaller"
              :items="callerOptions"
              item-text="text"
              item-value="value"
              placeholder="User..."
              solo
              flat
              dense
              hide-details
              clearable
              class="table-search raw-filter-field"
            ></v-autocomplete>
            <v-select
              v-model="rawFilterModel"
              :items="modelOptions"
              placeholder="Model..."
              solo
              flat
              dense
              hide-details
              clearable
              class="table-search raw-filter-field"
            ></v-select>
            <span class="raw-count">{{ filteredRawData.length }} records</span>
          </div>
        </div>

        <v-data-table
          :headers="rawHeaders"
          :items="filteredRawData"
          :pagination.sync="rawPagination"
          class="cost-table"
          dense
        >
          <template slot="items" slot-scope="{ item }">
            <tr>
              <td>{{ formatDate(item.Date) }}</td>
              <td>{{ item.Name || (item.CallerId ? callerNameMap[item.CallerId] : '') }}</td>
              <td>{{ item.CallerId }}</td>
              <td>{{ item.Provider }}</td>
              <td>
                <span class="cost-value"
                  >${{ (item.Cost || 0).toFixed(4) }}</span
                >
              </td>
              <td>
                <span class="truncated-text">{{ item.Question }}</span>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "AiCostReport",
  data() {
    const today = moment().format("YYYY-MM-DD");
    const thirtyDaysAgo = moment()
      .subtract(30, "days")
      .format("YYYY-MM-DD");

    return {
      loading: false,
      lastUpdated: null,
      tableSearch: "",
      tablePagination: {
        sortBy: "totalCost",
        descending: true,
        rowsPerPage: 10,
      },
      rawSearch: "",
      rawFilterCaller: null,
      rawFilterModel: null,
      rawPagination: { sortBy: "Date", descending: true, rowsPerPage: 10 },
      trendUserFilter: null,
      trendFrom: thirtyDaysAgo,
      trendTo: today,
      trendFromMenu: false,
      trendToMenu: false,
      trendGroupBy: "day",
      trendActiveRange: "30days",

      // Filters
      filters: {
        fromDate: thirtyDaysAgo,
        toDate: today,
        caller: "",
        model: null,
      },
      fromMenu: false,
      toMenu: false,

      modelOptions: [
        "Gemini 3 Pro Preview",
        "Gemini 3 Flash Preview",
        "Gemini 2.0 Flash",
        "GPT-4",
        "GPT-3.5",
      ],

      groupBy: "caller",
      groupByOptions: [
        { text: "User / Caller", value: "caller" },
        { text: "Day", value: "day" },
        { text: "Week", value: "week" },
        { text: "Month", value: "month" },
        { text: "Model", value: "model" },
      ],

      rawData: [],
    };
  },

  computed: {
    groupByLabel() {
      const opt = this.groupByOptions.find((o) => o.value === this.groupBy);
      return opt ? opt.text : this.groupBy;
    },

    totalCost() {
      return this.rawData.reduce((sum, r) => sum + (r.Cost || 0), 0);
    },

    totalQuestions() {
      return this.rawData.length;
    },

    avgCost() {
      return this.totalQuestions > 0 ? this.totalCost / this.totalQuestions : 0;
    },

    uniqueCallers() {
      return new Set(this.rawData.map((r) => r.CallerId)).size;
    },

    callerNameMap() {
      // Build CallerId -> best name mapping (use name if found in any record)
      const map = {};
      this.rawData.forEach((r) => {
        if (!r.CallerId) return;
        if (!map[r.CallerId] && r.Name) map[r.CallerId] = r.Name;
      });
      return map;
    },

    callerOptions() {
      // Aggregate costs by CallerId
      const costs = {};
      this.rawData.forEach((r) => {
        if (!r.CallerId) return;
        if (!costs[r.CallerId]) costs[r.CallerId] = 0;
        costs[r.CallerId] += r.Cost || 0;
      });
      // Return as { text: name or callerId, value: callerId } sorted by cost desc
      return Object.keys(costs)
        .sort((a, b) => costs[b] - costs[a])
        .map((callerId) => ({
          text: this.callerNameMap[callerId] || callerId,
          value: callerId,
        }));
    },

    tableHeaders() {
      const base = [
        { text: "Count", value: "count", width: "80px" },
        { text: "Total Cost", value: "totalCost", width: "120px" },
        { text: "Avg Cost", value: "avgCost", width: "120px" },
        { text: "% of Total", value: "percentage" },
      ];
      if (this.groupBy === "caller") {
        return [{ text: "User / Caller", value: "label" }, ...base];
      } else if (this.groupBy === "model") {
        return [{ text: "Model", value: "label" }, ...base];
      } else {
        return [{ text: "Period", value: "label" }, ...base];
      }
    },

    tableData() {
      const groups = {};

      this.rawData.forEach((item) => {
        let key;
        if (this.groupBy === "caller") {
          key = (item.CallerId ? (this.callerNameMap[item.CallerId] || item.CallerId) : item.Name) || "Unknown";
        } else if (this.groupBy === "day") {
          key = moment(item.Date).format("MMM D, YYYY");
        } else if (this.groupBy === "week") {
          key =
            "Week of " +
            moment(item.Date)
              .startOf("week")
              .format("MMM D, YYYY");
        } else if (this.groupBy === "month") {
          key = moment(item.Date).format("MMMM YYYY");
        } else if (this.groupBy === "model") {
          key = item.Provider || "Unknown";
        }

        if (!groups[key]) {
          groups[key] = { label: key, count: 0, totalCost: 0 };
        }
        groups[key].count++;
        groups[key].totalCost += item.Cost || 0;
      });

      return Object.values(groups)
        .map((g) => ({
          ...g,
          avgCost: g.count > 0 ? g.totalCost / g.count : 0,
          percentage:
            this.totalCost > 0 ? (g.totalCost / this.totalCost) * 100 : 0,
        }))
        .sort((a, b) => b.totalCost - a.totalCost);
    },

    filteredTableData() {
      if (!this.tableSearch) return this.tableData;
      const s = this.tableSearch.toLowerCase();
      return this.tableData.filter(
        (r) => r.label && r.label.toLowerCase().includes(s)
      );
    },

    chartSlices() {
      const palette = [
        "#4caf50", "#2196f3", "#ff9800", "#9c27b0", "#f44336",
        "#00bcd4", "#ff5722", "#3f51b5", "#8bc34a", "#e91e63",
        "#009688", "#ffc107", "#673ab7", "#03a9f4", "#cddc39",
      ];
      const r = 70;
      const circumference = 2 * Math.PI * r;
      const top = this.tableData.slice(0, 10);
      const total = top.reduce((s, g) => s + g.totalCost, 0);
      if (!total) return [];
      // Each circle has dasharray = [dash, gap] and dashoffset shifts where dash starts.
      // dashoffset = circumference - accumulated means: skip `accumulated` before drawing.
      let accumulated = 0;
      return top.map((g, i) => {
        const dash = (g.totalCost / total) * circumference;
        const gap = circumference - dash;
        const offset = circumference - accumulated;
        accumulated += dash;
        return {
          label: g.label,
          color: palette[i % palette.length],
          pct: (g.totalCost / total) * 100,
          dash,
          gap,
          offset,
        };
      });
    },

    trendSvgData() {
      let data = this.rawData;

      // Filter by trend date range using string prefix (YYYY-MM-DD) — safe for ISO dates
      if (this.trendFrom) {
        data = data.filter((r) => r.Date && r.Date.substring(0, 10) >= this.trendFrom);
      }
      if (this.trendTo) {
        data = data.filter((r) => r.Date && r.Date.substring(0, 10) <= this.trendTo);
      }
      if (this.trendUserFilter) {
        data = data.filter((r) => r.CallerId === this.trendUserFilter);
      }

      const byDay = {};
      data.forEach((r) => {
        let key, label;
        if (this.trendGroupBy === "week") {
          const weekStart = moment(r.Date).startOf("isoWeek");
          key = weekStart.format("YYYY-MM-DD");
          label = weekStart.format("MMM D");
        } else if (this.trendGroupBy === "month") {
          key = moment(r.Date).format("YYYY-MM");
          label = moment(r.Date).format("MMM YYYY");
        } else {
          key = moment(r.Date).format("YYYY-MM-DD");
          label = moment(r.Date).format("MMM D");
        }
        if (!byDay[key]) {
          byDay[key] = { date: key, label, cost: 0, count: 0 };
        }
        byDay[key].cost += r.Cost || 0;
        byDay[key].count++;
      });

      const points = Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date));
      if (points.length < 1) return null;

      const W = 900, H = 240;
      const padL = 68, padR = 30, padT = 20, padB = 40;
      const chartW = W - padL - padR;
      const chartH = H - padT - padB;

      const maxCost = Math.max(...points.map((p) => p.cost)) || 1;

      // For a single point, center it; for multiple, spread across full width
      const pts = points.map((p, i) => ({
        ...p,
        x: points.length === 1
          ? padL + chartW / 2
          : padL + (i / (points.length - 1)) * chartW,
        y: padT + (1 - p.cost / maxCost) * chartH,
      }));

      const linePoints = pts.length > 1 ? pts.map((p) => `${p.x},${p.y}`).join(" ") : "";
      const areaPath = pts.length > 1
        ? `M ${pts[0].x},${padT + chartH} ` +
          pts.map((p) => `L ${p.x},${p.y}`).join(" ") +
          ` L ${pts[pts.length - 1].x},${padT + chartH} Z`
        : "";

      const yLabels = Array.from({ length: 5 }, (_, i) => ({
        val: "$" + ((maxCost * i) / 4).toFixed(3),
        y: padT + (1 - i / 4) * chartH,
      }));

      const maxXLabels = 10;
      const step = Math.max(1, Math.ceil(points.length / maxXLabels));
      const xLabels = pts.filter((_, i) => i % step === 0 || i === pts.length - 1);

      return { W, H, padL, padR, padT, padB, chartW, chartH, pts, linePoints, areaPath, yLabels, xLabels };
    },

    filteredRawData() {
      return this.rawData.filter((r) => {
        if (this.rawFilterCaller && r.CallerId !== this.rawFilterCaller) return false;
        if (this.rawFilterModel && r.Provider !== this.rawFilterModel) return false;
        if (this.rawSearch) {
          const s = this.rawSearch.toLowerCase();
          return (
            (r.Question || "").toLowerCase().includes(s) ||
            (r.Name || "").toLowerCase().includes(s) ||
            (r.CallerId || "").toLowerCase().includes(s)
          );
        }
        return true;
      });
    },

    rawHeaders() {
      return [
        { text: "Date", value: "Date", width: "160px" },
        { text: "User", value: "Name", width: "140px" },
        { text: "Caller ID", value: "CallerId", width: "130px" },
        { text: "Model", value: "Provider", width: "180px" },
        { text: "Cost", value: "Cost", width: "90px" },
        { text: "Question", value: "Question" },
      ];
    },
  },

  created() {
    this.loadData();
  },

  methods: {
    getDomain() {
      return window.location.origin.startsWith("http://localhost")
        ? "https://localhost:5001"
        : "https://wercbergerproductions1-kha7mhdahq-ue.a.run.app";
    },

    setRange(range) {
      const today = moment().format("YYYY-MM-DD");
      const maps = {
        today: [today, today],
        week: [
          moment()
            .startOf("week")
            .format("YYYY-MM-DD"),
          today,
        ],
        month: [
          moment()
            .startOf("month")
            .format("YYYY-MM-DD"),
          today,
        ],
        "30days": [
          moment()
            .subtract(30, "days")
            .format("YYYY-MM-DD"),
          today,
        ],
        "90days": [
          moment()
            .subtract(90, "days")
            .format("YYYY-MM-DD"),
          today,
        ],
      };
      if (maps[range]) {
        this.filters.fromDate = maps[range][0];
        this.filters.toDate = maps[range][1];
        this.loadData();
      }
    },

    setTrendRange(range) {
      const today = moment().format("YYYY-MM-DD");
      const maps = {
        today:   [today, today],
        "7days": [moment().subtract(6, "days").format("YYYY-MM-DD"), today],
        "30days":[moment().subtract(29, "days").format("YYYY-MM-DD"), today],
        "90days":[moment().subtract(89, "days").format("YYYY-MM-DD"), today],
        month:   [moment().startOf("month").format("YYYY-MM-DD"), today],
      };
      if (maps[range]) {
        this.trendFrom = maps[range][0];
        this.trendTo = maps[range][1];
        this.trendActiveRange = range;
      }
    },

    async loadData() {
      this.loading = true;

      try {
        let filter = `Date ge ${this.filters.fromDate}T00:00:00Z`;
        filter += ` and Date le ${this.filters.toDate}T23:59:59Z`;

        if (this.filters.caller) {
          filter += ` and CallerId eq '${this.filters.caller}'`;
        }
        if (this.filters.model) {
          filter += ` and Provider eq '${this.filters.model}'`;
        }

        const url = `${this.getDomain()}/odata/aiQuestions?$filter=${encodeURIComponent(
          filter
        )}&$orderby=Date desc&$top=2000`;
        const response = await this.$http.get(url);
        this.rawData = response.data.value || [];
        this.lastUpdated = moment().format("MMM D, h:mm A");
      } catch (e) {
        console.error("Error loading cost report:", e);
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      return moment(date).format("MMM D, YYYY h:mm A");
    },
  },
};
</script>

<style scoped>
.cost-report-wrapper {
  padding: 0;
  min-height: 100vh;
  background: #f0f2f5;
  padding-bottom: 40px;
}

/* Header */
.report-header {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  padding: 8px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
}

.report-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back-btn {
  color: white !important;
}

.refresh-btn {
  color: white !important;
}

.report-title {
  font-size: 20px;
  font-weight: 600;
  color: white;
}

.report-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

/* Filters */
.filters-bar {
  background: white;
  margin: 16px 24px 0;
  border-radius: 12px;
  padding: 16px 20px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.filters-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.filter-field {
  width: 190px;
  flex: 0 0 190px;
}

.filter-field-sm {
  width: 160px;
  flex: 0 0 160px;
}

/* Force single line on select/autocomplete selections */
.filter-field >>> .v-select__selection,
.filter-field >>> .v-select__selections input {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-wrap: nowrap;
}

.filter-field >>> .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
}

.apply-btn {
  height: 40px !important;
  flex-shrink: 0;
}

.quick-ranges {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.quick-label {
  font-size: 12px;
  color: #888;
  margin-right: 4px;
}

/* Loading */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #667781;
  font-size: 16px;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  padding: 20px 24px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.summary-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-icon.green {
  background: #4caf50;
}
.summary-icon.blue {
  background: #2196f3;
}
.summary-icon.orange {
  background: #ff9800;
}
.summary-icon.purple {
  background: #9c27b0;
}

.summary-value {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.2;
}

.summary-label {
  font-size: 13px;
  color: #888;
  margin-top: 4px;
}

/* Table Section */
.table-section {
  margin: 0 24px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.table-chart-row {
  display: flex;
  align-items: flex-start;
}

.breakdown-table {
  flex: 1;
  min-width: 0;
}

.chart-panel {
  width: 380px;
  flex: 0 0 380px;
  padding: 20px 24px 16px;
  border-left: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.chart-title {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  align-self: flex-start;
}

.donut-wrapper {
  width: 220px;
  height: 220px;
  flex-shrink: 0;
}


.chart-legend {
  width: 100%;
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 220px;
  overflow-y: auto;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #444;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.legend-pct {
  font-weight: 600;
  color: #555;
  flex-shrink: 0;
}

.table-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.table-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
}

.table-search {
  max-width: 240px;
}

.table-search >>> .v-input__slot {
  box-shadow: none !important;
  border-bottom: 1px solid #ddd;
  border-radius: 0 !important;
  background: transparent !important;
  padding: 0 4px !important;
  min-height: 32px !important;
  margin-bottom: 0 !important;
}

.table-search >>> .v-input__slot:hover {
  border-bottom-color: #4caf50;
}

.table-search >>> .v-text-field__slot {
  padding: 0 !important;
}

.table-search >>> .v-text-field__slot input {
  font-size: 14px;
  color: #555;
  padding: 4px 0 4px !important;
  margin: 0 !important;
}

.table-search >>> .v-input__prepend-inner {
  margin-top: 4px !important;
  padding: 0 4px 0 0 !important;
}

.cost-value {
  font-weight: 600;
  color: #4caf50;
}

.cost-value-light {
  font-weight: 500;
  color: #888;
}

.percentage-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 140px;
}

.percentage-bar-bg {
  flex: 1;
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
}

.percentage-bar {
  height: 100%;
  background: #4caf50;
  border-radius: 3px;
  transition: width 0.3s;
}

.percentage-text {
  font-size: 12px;
  color: #888;
  min-width: 38px;
  text-align: right;
}

/* Fix date picker button colors overridden by global .v-btn { color: white !important } */
.v-date-picker-table .v-btn {
  color: rgba(0, 0, 0, 0.87) !important;
  background-color: transparent !important;
}
.v-date-picker-table .v-btn.v-btn--active,
.v-date-picker-table .v-btn.accent {
  color: white !important;
}
.v-date-picker-header .v-btn {
  color: rgba(0, 0, 0, 0.87) !important;
  background-color: transparent !important;
}
.v-date-picker-years li.active {
  color: #4caf50 !important;
}

/* Trend Chart */
.trend-section {
  margin: 0 24px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.trend-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.trend-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.trend-quick-ranges {
  display: flex;
  align-items: center;
  gap: 6px;
}

.trend-range-btn {
  display: inline-block;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 500;
  color: #1976d2;
  border: 1px solid #90caf9;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  &:hover {
    background: #e3f2fd;
  }
  &.active {
    background: #1976d2;
    color: white;
    border-color: #1976d2;
  }
}

.trend-user-field {
  width: 180px;
  flex: 0 0 180px;
}

.trend-chart-wrapper {
  padding: 16px 20px 8px;
}

.trend-svg {
  width: 100%;
  display: block;
}

.trend-empty {
  padding: 40px;
  text-align: center;
  color: #bbb;
  font-size: 14px;
}

/* Raw Data */
.raw-section {
  margin: 0 24px 32px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.raw-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  flex-wrap: wrap;
  gap: 12px;
}

.raw-filters-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

.raw-filter-field {
  width: 200px;
  flex: 0 0 200px;
}

.raw-filter-field >>> .v-select__selection,
.raw-filter-field >>> input {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.raw-count {
  font-size: 13px;
  color: #888;
  white-space: nowrap;
}

.truncated-text {
  max-width: 400px;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: #555;
}

.cost-table >>> thead th {
  background-color: #f8f8f8 !important;
  color: #555 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.cost-table >>> tbody tr:hover {
  background-color: #f9fdf9 !important;
}

.cost-table-padded >>> thead th:first-child,
.cost-table-padded >>> tbody td:first-child {
  padding-left: 24px !important;
}

.cost-table-padded >>> thead th:last-child,
.cost-table-padded >>> tbody td:last-child {
  padding-right: 24px !important;
}

.td-padded {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

/* Responsive */
@media (max-width: 960px) {
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  .filters-row {
    flex-direction: column;
    align-items: stretch;
  }
  .filter-field {
    max-width: 100%;
  }
}

@media (max-width: 600px) {
  .summary-cards {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 12px;
  }
  .table-section,
  .raw-section {
    margin: 0 12px 16px;
  }
}
.v-date-picker-table .v-btn {
  color: rgba(0, 0, 0, 0.87) !important;
}
.date-picker-menu button {
  background-color: #fff !important;
  color: rgba(0, 0, 0, 0.87) !important;
}
.v-date-picker-table .v-btn {
  color: rgba(0, 0, 0, 0.87) !important;
}
.v-date-picker-table .v-btn__content {
  color: black !important;
}
.v-date-picker-header .v-btn__content {
  color: black !important;
}
</style>
